import { Helmet } from 'react-helmet'

import { CopyButton } from '@/components/ui/CopyButton'
import { Cap, Demo } from '../../primitives'
import { PrototypeFrame } from '../../PrototypeFrame'
import { CaiProduct } from './navModel'
import { TabbedAccountSideNav } from './TabbedAccountSideNav'

interface StateDemo {
  key: string
  label: string
  /** Whether this combination can exist today, not whether the nav is drawn this way. */
  shipped: boolean
  /** The account this state is drawn for. */
  note?: string
  call: string
  nav: React.ReactNode
}

const LARGE_FLEET_PRODUCTS = [CaiProduct.INSURANCE, CaiProduct.VAULT]
const THREE_PRODUCTS = [CaiProduct.INSURANCE, CaiProduct.VAULT, CaiProduct.BOOKKEEPER]
const FOUR_PRODUCTS = [CaiProduct.INSURANCE, CaiProduct.VAULT, CaiProduct.BOOKKEEPER, CaiProduct.CFO]

const STATES: StateDemo[] = [
  {
    key: 'insurance',
    label: 'Insurance only',
    shipped: true,
    note: "If user's first Cai product is Insurance, and only has insurance",
    call: '<TabbedAccountSideNav products={[CaiProduct.INSURANCE]} />',
    nav: <TabbedAccountSideNav products={[CaiProduct.INSURANCE]} />,
  },
  {
    key: 'bookkeeper',
    label: 'Bookkeeping only',
    shipped: false,
    note: "If user's first Cai product is Bookkeeper or CFO",
    call: '<TabbedAccountSideNav products={[CaiProduct.BOOKKEEPER]} />',
    nav: <TabbedAccountSideNav products={[CaiProduct.BOOKKEEPER]} />,
  },
  {
    key: 'three-products',
    label: 'All three products',
    shipped: false,
    note: 'If user has all three products active',
    call: '<TabbedAccountSideNav products={THREE_PRODUCTS} />',
    nav: <TabbedAccountSideNav products={THREE_PRODUCTS} />,
  },
  {
    key: 'four-products',
    label: 'All four products',
    shipped: false,
    note: 'If user has all four products active',
    call: '<TabbedAccountSideNav products={FOUR_PRODUCTS} />',
    nav: <TabbedAccountSideNav products={FOUR_PRODUCTS} />,
  },
  {
    key: 'large-fleet',
    label: 'Large fleet — not eligible for finance',
    shipped: false,
    note: 'If user is too big for finance product eligibility based on our insurance visibility',
    call: '<TabbedAccountSideNav products={LARGE_FLEET_PRODUCTS} hidePitchFor={[CaiProduct.BOOKKEEPER]} />',
    nav: <TabbedAccountSideNav products={LARGE_FLEET_PRODUCTS} hidePitchFor={[CaiProduct.BOOKKEEPER]} />,
  },
]

const BRIEF = (
  <div className="border-ink-700 grid grid-cols-1 gap-6 border-t pt-6 sm:grid-cols-2 lg:grid-cols-5">
    <div>
      <Cap onDark>Goal</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        Lift the share of customers using more than one Cai product. That number is near zero today: of 280
        orgs in production only 10 are active, the vault is the only second product with an in-app surface,
        and bookkeeping and CFO have no in-app path at all &mdash; every signup comes off a marketing page.
        Confirm the live figure in PostHog before this ships.
      </p>
    </div>

    <div>
      <Cap onDark>Project: Multi-product account</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        The side nav is the only place in the app where a customer meets a product they have not bought. It is
        built today as if insurance is the only thing Cai sells.
      </p>
    </div>

    <div>
      <Cap onDark>What is real</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        The Insurance tab is the production nav, minus the Compliance vault. Everything else is proposed:
        bookkeeping and CFO are waitlists on <code>/bookkeeper</code> and <code>/cfo</code> with no in-app
        surface, and so are the tabs themselves. These render a prototype nav, not the live{' '}
        <code>AppSidebar</code>.
      </p>
    </div>

    <div>
      <Cap onDark>Feedback</Cap>
      <ul className="text-ink-200 mt-2 flex flex-col gap-1 text-xs leading-relaxed">
        <li>
          Slack channel:{' '}
          <a
            href="https://cailabsinc.slack.com/archives/C0C2CA57DQW"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-300 hover:text-brand-200 underline underline-offset-2"
          >
            #test-cai-bookkeeper
          </a>
        </li>
      </ul>
    </div>

    <div>
      <Cap onDark>Next</Cap>
      <p className="text-ink-200 mt-2 text-xs leading-relaxed">
        Three asks: a product-enablement field to gate on, since per-product feature flags do not describe
        what an account bought; a source for finance eligibility, which is a different question from
        enablement; and a home for Dashboard and Documents, which sit inside the Insurance tab and so are
        unreachable without coverage.
      </p>
    </div>
  </div>
)

export function AccountSideNavPrototypePage() {
  return (
    <PrototypeFrame
      title="Account side nav V1.0"
      owner="Carrie Phillips"
      date="September 18, 2026"
      status="Pre-production prototype"
      brief={BRIEF}
    >
      <Helmet>
        <title>Prototype: Account side nav V1.0</title>
      </Helmet>

      <Demo label="Product tabs under the carrier name — tabs are clickable">
        <div className="flex gap-6 overflow-x-auto pb-2">
          {STATES.map((state) => (
            <div key={state.key} className="flex shrink-0 flex-col gap-2">
              {state.note && <p className="text-ink-500 w-64 grow text-xs leading-relaxed">{state.note}</p>}
              {state.nav}
              <Cap className="flex items-center gap-1">
                {state.label} — {state.shipped ? 'in production' : 'proposed'}
                <CopyButton
                  value={state.call}
                  className="text-ink-400 hover:text-ink size-4 bg-transparent hover:bg-transparent"
                />
              </Cap>
            </div>
          ))}
        </div>
      </Demo>
    </PrototypeFrame>
  )
}
